/**
 * かんたん画面のホーム（らくらくフォン風）。
 *
 * ねらい: 1画面に出す情報を絞り、押すところを大きくする。
 * 打刻そのものは通常の打刻画面（/home）を使うため、打刻の仕組みは二重に持たない。
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../services/api';
import type { AuthUser } from '../hooks/useAuth';
import { setSimpleMode } from '../utils/simpleMode';
import { hasFeature } from '../utils/featureFlags';
import { BottomNav } from '../components/BottomNav';
import { AppHeader } from '../components/AppHeader';

interface Props { user: AuthUser; }

type Status = 'idle' | 'working' | 'unknown';

const BIG: React.CSSProperties = {
  width: '100%', border: 'none', borderRadius: 20, cursor: 'pointer',
  display: 'flex', alignItems: 'center', gap: 16, padding: '22px 22px',
  fontWeight: 900, textAlign: 'left', boxShadow: '0 3px 10px rgba(0,0,0,0.10)',
};

export const SimpleHomePage: React.FC<Props> = ({ user }) => {
  const nav = useNavigate();
  const [now, setNow] = useState(new Date());
  const [status, setStatus] = useState<Status>('unknown');
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000 * 20);
    return () => clearInterval(t);
  }, []);

  // 今の状態（業務中かどうか）だけを取る。かんたん画面ではこれ以上の情報は出さない
  useEffect(() => {
    apiFetch<{ data?: { session_status?: string } | null }>('/timestamps/sessions/active/current')
      .then(r => setStatus(r?.data?.session_status === 'working' || r?.data?.session_status === 'resting' ? 'working' : 'idle'))
      .catch(() => setStatus('unknown'));
    apiFetch<{ data?: { dms?: number } }>('/messages/unread-count')
      .then(r => setUnread(r?.data?.dms ?? 0))
      .catch(() => { /* 取れなければ出さない */ });
  }, []);

  const date = now.toLocaleDateString('ja-JP', { month: 'long', day: 'numeric', weekday: 'short', timeZone: 'Asia/Tokyo' });
  const hhmm = now.toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Asia/Tokyo' });

  return (
    <>
    <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch', background: '#f1f5f9', display: 'flex', flexDirection: 'column' }}>
      {/* 見出し（ノッチ・カメラ穴の下から始める。ここには押すものを置かない） */}
      <AppHeader title={`${user.user_name} さん`} subtitle="かんたん画面" back={false} padX={20}
        style={{ borderBottom: 'none', boxShadow: 'none' }} />

      {/* 日付と時刻 */}
      <div style={{ background: '#fff', padding: '0 20px 20px', textAlign: 'center', borderBottom: '2px solid #e2e8f0' }}>
        <div style={{ fontSize: 20, fontWeight: 800, color: '#334155' }}>{date}</div>
        <div style={{ fontSize: 56, fontWeight: 900, color: '#0f172a', lineHeight: 1.1, letterSpacing: '-2px' }}>{hhmm}</div>
        {status !== 'unknown' && (
          <div style={{
            display: 'inline-block', marginTop: 12, padding: '8px 22px', borderRadius: 999,
            fontSize: 19, fontWeight: 900,
            background: status === 'working' ? '#ecfdf5' : '#f1f5f9',
            color: status === 'working' ? '#047857' : '#475569',
            border: `2px solid ${status === 'working' ? '#6ee7b7' : '#cbd5e1'}`,
          }}>
            {status === 'working' ? 'いま 業務中' : 'いま 業務前'}
          </div>
        )}
      </div>

      {/* 大きなボタン。押すところは4つだけにする */}
      <div style={{ padding: '18px 16px 8px', display: 'flex', flexDirection: 'column', gap: 14 }}>
        <button onClick={() => nav('/home')}
          style={{ ...BIG, background: status === 'working' ? 'linear-gradient(145deg,#dc2626,#b91c1c)' : 'linear-gradient(145deg,#059669,#047857)', color: '#fff', padding: '30px 22px' }}>
          <span style={{ fontSize: 44, lineHeight: 1 }}>{status === 'working' ? '🏁' : '▶'}</span>
          <span>
            <span style={{ display: 'block', fontSize: 30, lineHeight: 1.2 }}>
              {status === 'working' ? '業務を終わる' : '業務を始める'}
            </span>
            <span style={{ display: 'block', fontSize: 15, fontWeight: 700, opacity: 0.9, marginTop: 4 }}>ここを押して打刻します</span>
          </span>
        </button>

        <button onClick={() => nav('/history')} style={{ ...BIG, background: '#fff', color: '#0f172a' }}>
          <span style={{ fontSize: 38, lineHeight: 1 }}>📋</span>
          <span style={{ fontSize: 24 }}>じぶんの記録をみる</span>
        </button>

        <button onClick={() => nav('/messages')} style={{ ...BIG, background: '#fff', color: '#0f172a', position: 'relative' }}>
          <span style={{ fontSize: 38, lineHeight: 1 }}>💬</span>
          <span style={{ fontSize: 24 }}>連絡をみる</span>
          {unread > 0 && (
            <span style={{ marginLeft: 'auto', background: '#dc2626', color: '#fff', borderRadius: 999, minWidth: 38, height: 38, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 19, fontWeight: 900 }}>
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </button>

        <button onClick={() => nav('/leave')} style={{ ...BIG, background: '#fff', color: '#0f172a' }}>
          <span style={{ fontSize: 38, lineHeight: 1 }}>🌴</span>
          <span style={{ fontSize: 24 }}>休みを申し込む</span>
        </button>

        {/* 乗下船表（公開されている会社のみ） */}
        {hasFeature('crew_boarding_sheet') && (
          <button onClick={() => nav('/boarding-sheet')} style={{ ...BIG, background: '#fff', color: '#0f172a' }}>
            <span style={{ fontSize: 38, lineHeight: 1 }}>🚢</span>
            <span style={{ fontSize: 24 }}>乗下船表をみる</span>
          </button>
        )}
      </div>

      {/* 通常画面へ戻る（設定を開かなくても戻せるようにする） */}
      <div style={{ padding: '18px 16px 28px', marginTop: 'auto' }}>
        <button onClick={() => { setSimpleMode(false); nav('/home'); }}
          style={{ width: '100%', padding: '16px', borderRadius: 14, border: '2px solid #cbd5e1', background: '#fff', color: '#475569', fontSize: 17, fontWeight: 800, cursor: 'pointer' }}>
          ふつうの画面にもどす
        </button>
      </div>
    </div>
    <BottomNav />
    </>
  );
};
